import { QueryRender } from "@/components/helpers/QueryRender"
import { CopyableText } from "@/components/ui/CopyableText"
import {
   createColumnHelper,
   flexRender,
   getCoreRowModel,
   getSortedRowModel,
   useReactTable,
} from "@tanstack/react-table"
import { Link, useNavigate } from "react-router-dom"
import { UserAvatar } from "../../components/user/UserAvatar"
import { UserRoleBadge } from "../../components/user/UserRoleBadge"
import { User, UserRole } from "../../entities/user/user"
import { useUsers } from "../../entities/user/useUser"
import { useState, useMemo } from "react"
import { UserFilterBar } from "@/components/user/UserFilterBar"
import { SearchNameFilterBar } from "@/components/user/SearchNameFilterBar"
import { formatPhoneNumber } from "@/utils/formatPhoneNumber"
import { UserSmallProfile } from "@/components/ui/UserSmallProfile"
import clsx from "clsx"

const columnHelper = createColumnHelper<User>()

const columns = [
   columnHelper.accessor("is_active", {
      header: "Statut",
      cell: (info) =>
         info.getValue() ? (
            <svg className="h-5 w-5 text-success" fill="currentColor" viewBox="0 0 24 24">
               <path d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm0-2a8 8 0 100-16 8 8 0 000 16zm4-8a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
         ) : (
            <svg className="h-5 w-5 text-error" fill="currentColor" viewBox="0 0 24 24">
               <path d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm0-2a8 8 0 100-16 8 8 0 000 16zm4-8a4 4 0 11-8 0 4 4 0 018 0zm-4 0a2 2 0 100 4 2 2 0 000-4z" />
            </svg>
         ),
   }),
   columnHelper.accessor((row) => `${row.firstName} ${row.lastName}`, {
      id: "fullName",
      header: "Nom",
      cell: (info) => (
         <div className="flex items-center gap-3">
            <UserAvatar photo={info.row.original.photo} name={info.getValue()} size="sm" />
            <div className="font-medium">{info.getValue()}</div>
         </div>
      ),
   }),
   columnHelper.accessor("email", {
      header: "Email",
      cell: (info) => (
         <div className="text-center">
            <CopyableText text={info.getValue()} />
         </div>
      ),
   }),
   columnHelper.accessor("phone", {
      header: "Téléphone",
      cell: (info) => (
         <div className="text-center">
            <CopyableText text={formatPhoneNumber(info.getValue())} />
         </div>
      ),
   }),
   columnHelper.accessor("group", {
      header: "Groupe",
      cell: (info) => info.getValue(),
   }),
   columnHelper.accessor("role", {
      header: "Rôle",
      cell: (info) => <UserRoleBadge role={info.getValue()} />,
   }),
]

export default function Users() {
   const navigate = useNavigate()
   const { data: users, ...query } = useUsers()
   const [selectedStatus, setSelectedStatus] = useState<"all" | "active" | "inactive">("active")
   const [selectedRole, setSelectedRole] = useState<"all" | UserRole>("all")
   const [searchedText, setSearchedText] = useState("")

   const filteredUsers = useMemo(() => {
      if (!users) return []
      return users
         .filter((user) => {
            if (selectedStatus !== "all") {
               const isActive = selectedStatus === "active"
               if (user.is_active !== isActive) return false
            }
            if (selectedRole !== "all" && user.role !== selectedRole) return false
            if (searchedText) {
               const fullName = `${user.firstName} ${user.lastName}`.toLowerCase()
               if (!fullName.includes(searchedText.toLowerCase())) return false
            }
            return true
         })
         .sort((a, b) => a.firstName.localeCompare(b.firstName))
   }, [users, selectedStatus, selectedRole, searchedText])

   const table = useReactTable({
      data: filteredUsers,
      columns,
      state: {},
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
   })

   return (
      <div className="page-section">
         <div className="flex justify-between items-center mb-4">
            <h1 className="title">Utilisateurs</h1>
            <Link to="/admin/ajouter-utilisateur" className="btn btn-primary">
               Ajouter un utilisateur
            </Link>
         </div>

         <div className="mb-6">
            <div className="flex flex-wrap gap-4 items-center">
               <UserFilterBar
                  selectedStatus={selectedStatus}
                  setSelectedStatus={setSelectedStatus}
                  selectedRole={selectedRole}
                  setSelectedRole={setSelectedRole}
               />
               <div className="flex-grow">
                  <SearchNameFilterBar setSearchText={setSearchedText} />
               </div>
            </div>
         </div>

         <QueryRender query={query} data={users} emptyMessage="Aucun utilisateur trouvé">
            {() => (
               <>
                  <div className="overflow-x-auto overflow-y-clip">
                     <div className="hidden lg:block">
                        <table className="table w-full">
                           <thead>
                              {table.getHeaderGroups().map((headerGroup) => (
                                 <tr key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => (
                                       <th key={header.id}>
                                          {flexRender(
                                             header.column.columnDef.header,
                                             header.getContext(),
                                          )}
                                       </th>
                                    ))}
                                 </tr>
                              ))}
                           </thead>
                           <tbody>
                              {table.getRowModel().rows.map((row) => (
                                 <tr
                                    key={row.id}
                                    className="hover:bg-base-200 cursor-pointer"
                                    onClick={() => {
                                       const userId = row.original.id
                                       navigate(`/admin/utilisateur/${userId}`)
                                    }}
                                 >
                                    {row.getVisibleCells().map((cell) => (
                                       <td key={cell.id}>
                                          {flexRender(
                                             cell.column.columnDef.cell,
                                             cell.getContext(),
                                          )}
                                       </td>
                                    ))}
                                 </tr>
                              ))}
                           </tbody>
                        </table>
                     </div>
                  </div>

                  <div className="lg:hidden grid grid-cols-1 sm:grid-cols-2 gap-4">
                     {filteredUsers.map((user) => (
                        <Link key={user.id} to={`/admin/utilisateur/${user.id}`}>
                           <div
                              className={clsx(
                                 "card shadow-md hover:shadow-lg transition-shadow cursor-pointer",
                                 !user.is_active && "border-2 border-error",
                                 user.role === "admin" && "bg-warning/10",
                                 user.role === "young" && "bg-info/10",
                              )}
                           >
                              <div className="card-body p-4">
                                 <div className="flex items-center gap-3 w-full">
                                    <div className="_flex-none w-2/5">
                                       <UserSmallProfile user={user} />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                       <p className="text-sm break-words">{user.email}</p>
                                       <p className="text-sm mt-2">
                                          {formatPhoneNumber(user.phone)}
                                       </p>
                                    </div>
                                 </div>
                              </div>
                           </div>
                        </Link>
                     ))}
                  </div>
               </>
            )}
         </QueryRender>
      </div>
   )
}
